<template>
  <GeneralTheme :theme="configuration">
    <section
      :class="{
        'media-two-column-component overflow-hidden': true,
      }"
    >
      <div
        :class="{
          'mx-auto w-3/4 max-w-1920px lg:w-screen': true,
        }"
      >
        <div
          :class="{
            'grid grid-cols-12 gap-y-10 lg:gap-30px': true,
          }"
        >
          <div :class="primaryColumnClasses">
            <ParallaxThemeComponent :theme="primaryBlockThemeMobile">
              <PrimaryBlock
                v-if="primaryBlock"
                v-bind="primaryBlock"
                :class="{
                  'max-w-[520px] text-left': true,
                }"
              />
            </ParallaxThemeComponent>
          </div>
          <div :class="secondaryColumnClasses">
            <AnimatedImageBlockTheme :theme="animatedImageBlockTheme">
              <PtcPicture
                v-if="imageBlock && imageBlock.asset"
                provider="sanity"
                format="webp"
                :src="imageBlock.asset._ref"
                :alt="resolvedImage?.altText || 'Jumbo image two column'"
                :quality="90"
                :img-attrs="{
                  class: { 'mx-auto p-0 w-full lg:w-auto': true },
                }"
                :modifiers="{
                  crop: imageBlock.crop,
                  hotspot: imageBlock.hotspot,
                }"
                loading="lazy"
              />
            </AnimatedImageBlockTheme>
          </div>
        </div>
      </div>
    </section>
  </GeneralTheme>
</template>

<script setup lang="ts">
import type {
  MediaTwoColumnPrimaryBlock,
  ParallaxTheme,
  SectionBuilderJumboImageTwoColumn,
} from '~/sanity/types';
import ParallaxThemeComponent from '~/components/Molecules/Themes/ParallaxTheme.vue';
import { useResponsive } from '~/composables/useResponsive';
import AnimatedImageBlockTheme from '~/components/Molecules/Themes/AnimatedImageBlockTheme.vue';
import PtcPicture from '~/components/Atoms/PtcPicture/PtcPicture.vue';
import GeneralTheme from '~/components/Molecules/Themes/GeneralTheme.vue';
import PrimaryBlock from './PrimaryBlock.vue';
const props = defineProps<SectionBuilderJumboImageTwoColumn>();
const { primaryBlock, imageBlock } = props.jumboImageTwoColumn ?? {};

const { getResolvedImage } = useRefsStore();
const resolvedImage = await getResolvedImage(imageBlock?.asset?._ref);

type WidthType = NonNullable<MediaTwoColumnPrimaryBlock['width']>;

//Initialize widths dynamically based on props or logic
const primaryWidth = ref<WidthType>(primaryBlock?.width || '6');
const secondaryWidth = computed(() => 12 - Number(primaryWidth.value));
const primaryAlignment = ref<string>(primaryBlock?.alignment || 'left');
const rs = useResponsive().getDefault();
const primaryBlockThemeMobile = computed<ParallaxTheme | undefined>(() => {
  if (!rs.isLg.value) {
    return {
      ...props.primaryBlockTheme,
      animation: 'fadeIn',
      _type: 'parallaxTheme',
    };
  }
  return props.primaryBlockTheme;
});

const primaryColumnClasses = computed(() => ({
  [widthStyles[primaryWidth.value] ?? '']: true,
  [alignmentStyles[primaryAlignment.value] ?? '']: true,
  'lg:w-3/4 col-span-12 h-full': true,
}));
const secondaryColumnClasses = computed(() => ({
  [widthStyles[secondaryWidth.value] ?? '']: true,
  [alignmentSecodaryStyles[primaryAlignment.value] ?? '']: true,
  'content-center col-span-12': true,
}));

const widthStyles: Record<string, string> = {
  '1': ' lg:col-span-1',
  '2': ' lg:col-span-2',
  '3': ' lg:col-span-3',
  '4': ' lg:col-span-4',
  '5': ' lg:col-span-5',
  '6': ' lg:col-span-6',
  '7': ' lg:col-span-7',
  '8': ' lg:col-span-8',
  '9': ' lg:col-span-9',
  '10': ' lg:col-span-10',
  '11': ' lg:col-span-11',
};

const alignmentStyles: Record<string, string> = {
  left: 'lg:order-0 lg:justify-self-end',
  right: 'lg:order-1 lg:justify-self-start',
};
const alignmentSecodaryStyles: Record<string, string> = {
  left: 'lg:order-1',
  right: 'lg:order-0',
};
</script>
